import Chip from "@mui/material/Chip";
import { useEffect, useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { CourseListPropType } from "src/models/Course.model";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/CourseSlice";
import { RootState } from "src/redux/store";
import {
  useDeleteCourseMutation,
  useGetCoursesQuery,
} from "src/services/CourseServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import AddCourseFormWrapper from "../Add/AddCourseFormWrapper";
import EditCourseFormWrapper from "../Edit/EditCourseFormWrapper";
import CourseListing from "./CourseListing";
import { UserModuleActionTypes, UserModuleNameTypes } from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const CourseListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.course);
  const [isOpenAddForm, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState("");

  const [deleteCourse] = useDeleteCourseMutation();
  const {
    data: courseData,
    isLoading: isCourseLoading,
    isFetching: isCourseFetching,
  } = useGetCoursesQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "courseName",
      "courseDescription",
      "classroomFees",
      "onlineFees",
      "moduleId",
    ],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  const getActionOptions = (courseId: string) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setSelectedCourseId(courseId);
          setIsOpenEditForm(true);
        },
        moduleName: UserModuleNameTypes.COURSES,
        actionName: UserModuleActionTypes.EDIT,
      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure, you want to Delete this Course ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteCourse({id:courseId}).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        }, 
        moduleName: UserModuleNameTypes.COURSES,
        actionName: UserModuleActionTypes.DELETE,
      },
    ];
  };

  const columns: columnTypes[] = [ 
    {
      field: "courseName",
      headerName: "Course",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[100px] py-2",
    },
    {
      field: "courseDescription",
      headerName: "Description",
      flex: "flex-[1.75_1.75_0%]",
      extraClasses: "min-w-[200px] py-2",
    },
    {
      field: "moduleId",
      headerName: "Modules",
      flex: "flex-[2_2_0%]", 
      extraClasses: "min-w-[160px] flex-wrap px-2",
      renderCell: (row: CourseListPropType) => {
        return (
          <>
            {row?.moduleId?.map(
              (module: { _id: string; moduleName: string }) => {
                return (
                  <Chip key={module?._id} label={module?.moduleName} className="me-2 my-2" />
                );
              }
            )}
          </>
        );
      },
    },
    {
      field: "classroomFees",
      headerName: "Classroom Fees",
      flex: "flex-[0.5_0.5_0%]",
      extraClasses: "min-w-[150px]",
      renderCell: (row: CourseListPropType) => {
        return <span>&#8377; {row?.classroomFees}</span>;
      },
    },
    {
      field: "onlineFees",
      headerName: "Online Fees",
      flex: "flex-[0.5_0.5_0%]",
      extraClasses: "min-w-[150px]",
      renderCell: (row: CourseListPropType) => {
        return <span>&#8377; {row?.onlineFees}</span>;
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: "flex-[0.25_0.25_0%]",
      extraClasses: "",
      renderCell: (row: CourseListPropType) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  useEffect(() => {
    if (!isCourseLoading && !isCourseFetching) {
      dispatch(setItems(courseData?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(courseData?.totalItem));
      dispatch(setRowsPerPage(courseData?.pageSize || 0));
      dispatch(setPage(courseData?.currentPage || 0));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseData, isCourseLoading, isCourseFetching]);

  return (
    <div>
      <CourseListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.COURSES,
          actionName: UserModuleActionTypes.LIST,
          columns: columns
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddForm ? (
        <AddCourseFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditForm ? (
        <EditCourseFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          courseId={selectedCourseId}
        />
      ) : null}
    </div>
  );
};

export default CourseListingWrapper;
